import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@material-ui/core";
import { uuid4 } from "@sentry/utils";

import ExpandMore from "@material-ui/icons/ExpandMore";
import React, { useContext, useImperativeHandle, useMemo } from "react";
import { Droppable } from "react-beautiful-dnd";
import { createPortal } from "react-dom";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { Option } from "space-monad";
import styled from "styled-components";
import { Button, DangerButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { Flex } from "../../../../components/layout";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { Field } from "../../../../store/launch-configurations/models/internal/field";
import { colours } from "../../../../styles/colours";
import { FieldSubSection } from "../common/components";
import MultiTextField from "../common/multi-lingual-text";
import SingleField from "./single-field";
import SingleFieldOverlay from "./single-field/overlay";

export interface ISinglePageRefType {
  removeItem: (index: number) => void;
  insertItem: (index: number, item: any) => void;
  moveItem: (indexFrom: number, indexTo: number) => void;
  getItem: (index: number) => Field.Field<string>;
}

interface ISinglePageProps {
  pageNumber: number;
  removePage: () => void;
  id: string;
}

const StyledAccordionDetails = styled(AccordionDetails)`
  display: block;
  & > * {
    margin: 0.5em;
  }
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 1em;
  box-shadow: none;
`;

const AccordionTitle = styled(Flex)`
  font-size: 1.3em;
  width: 100%;
  justify-content: space-between;
`;

const RemovePageButton = styled(DangerButton)`
  margin-left: 1em;
`;

const StyledAccordionSummary = styled(AccordionSummary)<{ error: boolean }>`
  font-size: 0.8em;

  border-radius: 13px;

  ${(props) => (props.error ? `border: 1px solid ${colours.redFailed};` : "")}
  ${(props) =>
    props.error ? `background: #ffe5e5;` : "background: whitesmoke;"}
`;

const SinglePage = React.forwardRef<ISinglePageRefType, ISinglePageProps>(
  ({ pageNumber, removePage, id }, ref) => {
    const {
      control,
    } = useFormContext<LaunchConfigurationsDTO.Requests.SetSingle.T>();

    const { fields, move, remove, append, insert } = useFieldArray({
      control,
      name: `pages.${pageNumber}.fields`,
    });

    const pageTitle = useWatch({
      control,
      name: `pages.${pageNumber}.title`,
    });

    const { getRef, setOverlay, closeOverlay } = useContext(
      GlobalOverlayContext
    );
    const uuid = useMemo(() => uuid4(), []);
    const ref1 = useMemo(() => getRef && getRef(uuid), [uuid, getRef]);

    useImperativeHandle(ref, () => ({
      removeItem(index: number) {
        remove(index);
      },
      insertItem(index: number, item: any) {
        insert(index, item);
      },
      moveItem(indexFrom: number, indexTo: number) {
        move(indexFrom, indexTo);
      },
      getItem(index: number): Field.Field<string> {
        return fields[index];
      },
    }));

    return (
      <StyledAccordion>
        <StyledAccordionSummary expandIcon={<ExpandMore />} error={false}>
          <AccordionTitle>
            {Option(pageTitle)
              .map((x) => x["en_GB"])
              .filter((x) => !!x)
              .getOrElse(`Page ${pageNumber + 1}`)}
            <RemovePageButton onClick={removePage}>
              <Icon type="trash" />
            </RemovePageButton>
          </AccordionTitle>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <FieldSubSection>
            <strong>Title</strong>
            <Controller
              control={control}
              name={`pages.${pageNumber}.title`}
              render={({ field }) => <MultiTextField {...field} />}
            />
          </FieldSubSection>
          <Flex justify="space-between">
            <strong style={{}}>Fields:</strong>
            <Button
              colour="secondary"
              variant="outlined"
              onClick={() => {
                setOverlay({
                  portalUUID: uuid,
                  title: "Field Details",
                });
              }}
            >
              <Icon type="plus" />
            </Button>
          </Flex>
          <Droppable droppableId={id}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {fields.map(({ id }, index) => (
                  <Controller
                    key={id}
                    control={control}
                    name={`pages.${pageNumber}.fields.${index}`}
                    render={({ field }) => (
                      <SingleField
                        remove={() => remove(index)}
                        index={index}
                        id={id}
                        {...field}
                      />
                    )}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {ref1
            ? createPortal(
                <SingleFieldOverlay
                  onSubmit={(data) => {
                    append(data);
                    closeOverlay();
                  }}
                />,
                ref1
              )
            : null}
        </StyledAccordionDetails>
      </StyledAccordion>
    );
  }
);

export default SinglePage;
